import { createTheme } from '@mui/material';

declare module '@mui/material/styles' {
  interface Palette {
    primaryLight: Palette['primary'];
    customGrey: Palette['primary'];
  }
  interface PaletteOptions {
    primaryLight?: PaletteOptions['primary'];
    customGrey?: PaletteOptions['primary'];
  }

  interface TypographyVariants {
    body3: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
  }

  interface Theme {
    boxShadow1: string;
    boxShadow2: string;
  }
  interface ThemeOptions {
    boxShadow1?: string;
    boxShadow2?: string;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
  }

  interface TypographyPropsColorOverrides {
    customGrey: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    primaryLight: true;
  }
}

declare module '@mui/material/CircularProgress' {
  interface CircularProgressPropsColorOverrides {
    white: true;
  }
}
export const akovaTheme = () => {
  const createColor = (mainColor: string) => createTheme().palette.augmentColor({ color: { main: mainColor } });
  const titleColor = '#003250';

  let theme = createTheme({
    palette: {
      primary: {
        main: '#5A87AA',
        light: '#BDCFDD',
        dark: '#003250',
        contrastText: '#fff'
      },
      secondary: createColor('#F5F5F5'),
      background: {
        default: '#EBEBE7'
      },
      success: createColor('#41A664'),
      error: createColor('#CB4949'),
      text: {
        primary: '#2a2a2a',
        secondary: '#000'
      },
      customGrey: createColor('#616161')
    },
    typography: {
      fontFamily: '"Noto Sans", sans-serif',
      fontSize: 16,
      h1: {
        fontSize: '1.5rem', // 24px
        color: titleColor
      },
      h2: {
        fontSize: '1.125rem', // 18px
        color: titleColor
      },
      body1: {
        fontSize: '1rem' // 16px
      },
      body2: {
        fontSize: '.875rem' // 14px
      },
      body3: {
        fontSize: '.75rem' // 12px
      }
    }
  });

  theme = createTheme(theme, {
    palette: {
      ...theme.palette,
      primaryLight: {
        main: theme.palette.primary.light,
        contrastText: theme.palette.primary.main
      }
    },
    boxShadow1: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    boxShadow2: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    shadows: [
      'none',
      '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
      '0px 4px 6px rgba(0, 0, 0, 0.1)',
      ...Array(22).fill('none')
    ],
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset, &:hover fieldset, &.Mui-focused fieldset': {
                borderColor: 'transparent !important'
              },
              '&.Mui-disabled': {
                WebkitTextFillColor: theme.palette.action.disabled,
                backgroundColor: theme.palette.action.disabledBackground
              }
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.2)',
            '&:hover, &.Mui-focused': {
              boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.4)'
            }
          },
          input: {
            padding: theme.spacing(3)
          }
        }
      },
      MuiButton: {
        defaultProps: {
          disableRipple: false
        },
        styleOverrides: {
          root: {
            maxWidth: '243px',
            fontSize: theme.typography.body1.fontSize,
            paddingTop: theme.spacing(0.625),
            paddingBottom: theme.spacing(0.625),
            paddingRight: theme.spacing(2),
            paddingleft: theme.spacing(2),
            textTransform: 'none'
          }
        }
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            '& .MuiFormControlLabel-label': {
              fontSize: theme.typography.body2.fontSize
            }
          }
        }
      },
      MuiToggleButton: {
        defaultProps: {
          disableRipple: false
        },
        styleOverrides: {
          root: {
            fontWeight: 'normal',
            color: theme.palette.text.primary,
            textTransform: 'none',
            backgroundColor: theme.palette.common.white,
            '&:hover': {
              backgroundColor: theme.palette.secondary.main
            },
            '&.Mui-selected': {
              backgroundColor: theme.palette.common.white,
              '&:hover': {
                backgroundColor: theme.palette.secondary.main
              }
            }
          }
        }
      },
      MuiLink: {
        styleOverrides: {
          root: {
            cursor: 'pointer',
            color: theme.palette.common.black
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            width: '100%',
            borderRadius:
              typeof theme.shape.borderRadius === 'number'
                ? theme.shape.borderRadius * 2
                : `calc(${theme.shape.borderRadius} * 2)`,
            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)'
          }
        }
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            backgroundColor: theme.palette.secondary.main,
            textAlign: 'center',
            padding: theme.spacing(1)
          },
          content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          },
          title: {
            fontSize: theme.typography.body2.fontSize
          },
          subheader: {
            fontWeight: 'bold',
            fontSize: theme.typography.body1.fontSize
          }
        }
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            '&:last-child': {
              paddingBottom: theme.spacing(1)
            }
          }
        }
      },
      MuiSvgIcon: {
        styleOverrides: {
          fontSizeLarge: {
            fontSize: '64px'
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: theme.shape.borderRadius,
            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
            width: '100%'
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            color: theme.palette.grey[800],
            fontSize: theme.typography.body3.fontSize,
            borderRadius: '100px',
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            variants: [
              {
                props: { color: 'primary' },
                style: {
                  color: theme.palette.common.white
                }
              }
            ]
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-track': {
              backgroundColor: theme.palette.secondary.dark
            }
          }
        }
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: theme.typography.body3.fontSize
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: theme.palette.common.white,
            boxShadow: 'none',
            borderRadius: '0',
            '& .MuiToolbar-root': {
              height: '90px'
            }
          }
        }
      }
    }
  });

  return theme;
};
